<template>
  <div ref="el" class="cmg-range-slider" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { API } from 'nouislider'
import { useNoUiSlider, type NoUiRange } from '../composables/useNoUiSlider'

// Vue 3 wrapper around noUiSlider. Two-way bound on a number via v-model.
// The imperative slider lifecycle lives in useNoUiSlider; this SFC is the
// declarative shell that ties the element ref + props to that composable.

const props = withDefaults(
  defineProps<{
    modelValue: number
    min: number
    max: number
    step?: number
    // Full noUiSlider range for non-linear per-segment stepping; wins over min/max when given.
    range?: NoUiRange | null
    formatTooltip?: ((value: number) => string) | null
  }>(),
  { step: 0.01, range: null, formatTooltip: null },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
  (e: 'slider-ready', instance: API): void
}>()

const el = ref<HTMLElement | null>(null)

const slider = useNoUiSlider(el, {
  start: props.modelValue,
  min: props.min,
  max: props.max,
  step: props.step,
  range: props.range ?? undefined,
  formatTooltip: props.formatTooltip,
  onChange: (value) => emit('update:modelValue', value),
})

onMounted(() => {
  slider.init()
  // Surface the underlying noUiSlider instance so the simulator's existing
  // helpers (adjustHourlyRate, updateHourlyWage, …) — which already drive
  // `slider.value.set(...)` — keep working unchanged.
  if (slider.instance.value) emit('slider-ready', slider.instance.value)
})

onBeforeUnmount(() => slider.destroy())

// Drive the slider when the parent updates the model from elsewhere
// (manual input, ± stepper). The composable suppresses the echo of the
// slider's own emissions, so this is safe.
watch(
  () => props.modelValue,
  (next) => {
    const numeric = Number(next)
    if (!Number.isNaN(numeric)) slider.setValue(numeric)
  },
)

// Re-apply range when min/max change (e.g. after API settings load).
watch([() => props.min, () => props.max], ([min, max]) => {
  slider.updateRange({ min, max })
})
</script>
